import { type EventHandler, type KeyboardEvent } from 'react';
import {
  Chip,
  Icon,
  RadioButton,
  Tag,
  Text,
  useBreakpoint
} from '@cencosud-cencommerce/paris-uikit';
import classNames from 'classnames';

import { SendShippingEvents } from '@/modules/shared/utils/gtmEvents';
import type { GtmGroupsData } from '@/modules/shared/utils/gtmEvents/types';
import {
  ShippingEventInteractiveActions,
  ShippingEventInteractiveCategories,
  ShippingEventInteractiveTags
} from '@/modules/shared/utils/gtmEvents/types';
import type { TimeSlotValue } from '@/modules/shippingMethod/domain/ShippingMethod';

export interface DeliveryDateItemProps {
  label: string;
  date: string;
  description?: string;
  price?: string;
  crossedOutPrice?: string;
  isFlash?: boolean;
  id: string;
  originNodeCode?: string;
  originNodeType?: string;
  originNodeAdmin?: string;
  logisticsModel?: string;
  deliveryDateFrom?: string;
  deliveryDateTo?: string;
  shippingType?: string;
  shippingSubType?: string;
  shippingMethod?: string;
  chips?: {
    label: string;
    value: TimeSlotValue;
    disabled?: boolean;
    selected?: boolean;
    onClick: () => void;
  }[];
  selected?: boolean;
  onlyOption?: boolean;
  onChange: (id: string) => void;
  gtmData?: GtmGroupsData;
  shippingCapability?: string;
}

const DeliveryDateItem: React.FC<DeliveryDateItemProps> = ({
  label,
  date,
  description,
  price,
  crossedOutPrice,
  isFlash,
  id,
  chips,
  selected = false,
  onlyOption = false,
  onChange,
  gtmData
}) => {
  const handleKeyDown: EventHandler<KeyboardEvent<HTMLElement>> = (
    event
  ): void => {
    const id = event.currentTarget.getAttribute('for');
    if (id && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      event.stopPropagation();
      onChange(id);
    }
  };

  const { isDesktop } = useBreakpoint();

  const gtmEventHandler = () => {
    let interactiveTag = ShippingEventInteractiveTags.ReceiveToday;

    if (label.toLowerCase().includes('mañana')) {
      interactiveTag = ShippingEventInteractiveTags.ReceiveTomorrow;
    } else if (label.toLowerCase().includes('entre')) {
      interactiveTag = ShippingEventInteractiveTags.ReceiveBetween;
    }

    SendShippingEvents.sendEvent({
      event: 'baseEvent',
      extraFields: {
        'event-interactive-accion':
          ShippingEventInteractiveActions.CheckoutDate,
        'event-interactive-category':
          ShippingEventInteractiveCategories.Checkout,
        'event-interactive-tag': interactiveTag,
        'event-interactive-grupo_despacho': `${gtmData?.groupNumber} - ${gtmData?.totalGroups}`
      }
    });
  };
  let priceValue;
  if (price) {
    priceValue = price;
  } else if (!price && crossedOutPrice && !selected) {
    priceValue = crossedOutPrice;
  } else {
    priceValue = undefined;
  }

  const handleDeliveryTimeGtmEvent = (deliveryTime: string) => {
    SendShippingEvents.sendEvent({
      event: 'baseEvent',
      extraFields: {
        'event-interactive-category':
          ShippingEventInteractiveCategories.Checkout,
        'event-interactive-tag': deliveryTime,
        'event-interactive-accion':
          ShippingEventInteractiveActions.ClickConfirmScheduled,
        'event-interactive-grupo_despacho': `${gtmData?.groupNumber} - ${gtmData?.totalGroups}`
      }
    });
  };

  return (
    <label
      role="button"
      tabIndex={0}
      htmlFor={id}
      data-testid="co-delivery-card-item"
      onKeyDown={handleKeyDown}
      className={classNames(
        'co-w-full co-flex co-flex-col co-p-3 co-gap-2',
        'co-border-t co-border-t-neutral-300',
        'focus:co-outline-2 focus:co-outline-offset-2 focus:co-outline-[--color-focus]'
      )}
    >
      <div className={classNames('co-flex co-justify-between')}>
        <div className="co-flex co-gap-2">
          <RadioButton
            id={id}
            variant="card"
            name={`${date}-${id}`}
            checked={selected}
            tabIndex={-1}
            onChange={() => onChange(id)}
            onClick={gtmEventHandler}
          />
          <Text variant="body2" weight={600}>
            {label}
          </Text>
          <Text variant="body2" weight={400}>
            {date}
          </Text>
        </div>
        <div className="co-flex co-items-center co-justify-center co-gap-1">
          {isFlash && (
            <Icon name="flash" className="co-w-5 co-h-5" fill="secondary-500" />
          )}
          <div className="co-flex co-items-center co-justify-center co-gap-2">
            {crossedOutPrice && selected && (
              <Text
                variant="caption"
                weight={500}
                color="neutral-900"
                className="co-line-through"
              >
                {crossedOutPrice}
              </Text>
            )}
            {priceValue && (
              <Text variant="body2" weight={700} color="primary-500">
                {priceValue}
              </Text>
            )}
            {!priceValue && <Tag>Gratis</Tag>}
          </div>
        </div>
      </div>
      {selected && chips && (
        <div className="co-flex co-flex-col co-gap-1 co-ms-7">
          <Text variant="body2" weight={400}>
            {description}
          </Text>
          {onlyOption && (
            <Tag variant="information" size="sm" className="co-w-fit">
              Única opción de entrega disponible
            </Tag>
          )}
          <div className="co-flex co-gap-2 co-overflow-x-auto">
            {chips.map((chip, index) => (
              <Chip
                size={isDesktop ? 'md' : 'sm'}
                onClick={() => {
                  chip.onClick();
                  handleDeliveryTimeGtmEvent(chip.label);
                }}
                disabled={chip.disabled}
                key={index}
                selected={chip.selected}
              >
                {chip.label}
              </Chip>
            ))}
          </div>
        </div>
      )}
    </label>
  );
};

export default DeliveryDateItem;
